import classNames from 'classnames';
import { GetStaticPathsContext, GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';
import React from 'react';

import { Link } from '../../components/atoms/link';
import { PageTemplate } from '../../components/templates/page-template';
import { DocsManifestType, ICategory, getDocFilePath, getDocsManifest } from '../../utils/docs';
import { renderMarkdownFile } from '../../utils/markdown';

interface Props {
  language: string;
  category: ICategory;
  content: string;
  title?: string;
  docsManifest: DocsManifestType;
  docSlug: string;
}

const DocsPage: React.FC<Props> = (props) => {
  let { title, content, category, docsManifest, docSlug } = props;
  let [openedCategory, setOpenedCategory] = React.useState(null);
  let [showManifest, setShowManifest] = React.useState(false);

  return (
    <PageTemplate title={title}>
      <div className="md:flex">
        <div className="w-full md:w-1/4 md:pr-4 md:mr-4 md:border-r-2 print:hidden">
          <div
            className="w-full pb-4 font-bold text-gray-500 text-center hover:text-gray-700 cursor-pointer block md:hidden uppercase"
            onClick={() => {
              setShowManifest(!showManifest);
            }}
          >
            {!showManifest ? 'Show Menu' : 'Hide Menu'}
          </div>

          <ul
            className={classNames({
              'hidden md:block': !showManifest,
              block: showManifest,
            })}
          >
            {docsManifest.map((c) => {
              let isActiveCategory = category.slug === c.slug;

              return (
                <li key={c.slug} className="mb-4">
                  <ul>
                    <li
                      className={classNames('font-medium cursor-pointer', {
                        'text-blue-500': isActiveCategory,
                        'text-gray-500': !isActiveCategory,
                      })}
                      onClick={() => {
                        if (openedCategory === c.slug) {
                          setOpenedCategory(null);
                        } else {
                          setOpenedCategory(c.slug);
                        }
                      }}
                    >
                      {c.title}
                    </li>

                    {openedCategory === c.slug
                      ? c.docs.map((d) => {
                          let isActiveDoc = isActiveCategory && docSlug === d.slug;

                          return (
                            <li
                              className={classNames('cursor-pointer my-1', {
                                'text-blue-500': isActiveDoc,
                                'text-gray-500 hover:text-gray-600': !isActiveDoc,
                              })}
                              key={d.slug}
                            >
                              <Link href="/docs/[...slug]" as={`/docs/${c.slug}/${d.slug}`}>
                                {d.title}
                              </Link>
                            </li>
                          );
                        })
                      : null}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="md-content md:w-3/4" dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    </PageTemplate>
  );
};

export function getStaticProps(context: GetStaticPropsContext): GetStaticPropsResult<Props> {
  try {
    let slug = context.params.slug;
    if (!Array.isArray(slug)) {
      throw new Error('Slug should be an array');
    }

    let locale: string = context.locale || context.defaultLocale;
    let docsManifest = getDocsManifest(locale);

    let categorySlug = slug[0];
    let docSlug = slug[1];

    let filePath = getDocFilePath(categorySlug, docSlug, locale);
    let data = renderMarkdownFile(filePath);

    return {
      props: {
        language: locale,
        content: data.content,
        title: data?.data?.title || '',
        category: docsManifest.find((c) => c.slug === categorySlug),
        docsManifest,
        docSlug,
      },
    };
  } catch (err) {
    console.error(err);

    return {
      notFound: true,
    };
  }
}

type StaticPath = { params: { slug: Array<string> }; locale: string };

export function getStaticPaths(context: GetStaticPathsContext): GetStaticPathsResult {
  let paths: Array<StaticPath> = [];

  let locales = context.locales || [];
  for (let locale of locales) {
    let docManifest = getDocsManifest(locale);
    for (let docCategory of docManifest) {
      for (let doc of docCategory.docs) {
        paths.push({
          params: {
            slug: [docCategory.slug, doc.slug],
          },
          locale,
        });
      }
    }
  }

  return {
    paths,
    fallback: false,
  };
}

export default DocsPage;
